/**
 * Tenant-admin gate (Phase 38 / GATE-03 / D-06 / D-07 / D-1.18).
 *
 * Predicado para a área de Administração do escritório. Diferente do
 * `super-admin.ts` (Phase 14), este gate é TENANT-scoped, não sede-scoped:
 *
 *   `isEscritorioAdmin(s) = s.role.ativo && s.role.administracao`
 *
 * CONTRASTE DELIBERADO com `super-admin.ts` (D-07 / Pitfall 7):
 *  - super-admin adiciona `s.escritorioid === PRINCIPAL_ESCRITORIO_ID`
 *    (sede-scoped, via env). Usar AQUELE gate aqui trancaria fora os admins
 *    de escritórios NÃO-principais da própria tela de admin deles.
 *  - Por isso este módulo NÃO importa `getPrincipalEscritorioId`,
 *    NÃO importa `isSuperAdmin` e NÃO referencia `PRINCIPAL_ESCRITORIO_ID`.
 *
 * D-1.18: os booleanos são soletrados explicitamente — `administracao` NÃO é
 * super-permissão implícita.
 *
 * D-06: TODA operação (incluindo leituras) é gated por `isEscritorioAdmin`.
 * UI escondida (engrenagem só-admin, Plan 38-03) é conveniência, NÃO a defesa;
 * a defesa real é este gate de servidor (página RSC + `require` no makeHandler).
 */
import type { SessionContext } from './types';

/**
 * Predicado puro do tenant-admin. Composição EXATA:
 *   `ativo && administracao`.
 *
 * NUNCA referencia `escritorioid` / `PRINCIPAL_ESCRITORIO_ID` — um admin ativo
 * de QUALQUER escritório passa (anti-IDOR é garantido em outra camada: o
 * `escritorioid` alvo vem sempre da sessão/`getTenant()`, nunca do body/params).
 */
export function isEscritorioAdmin(s: SessionContext): boolean {
  return s.role.ativo && s.role.administracao;
}

/**
 * Permissões por-operação da Administração (template `super-admin.ts` /
 * `configuracoes.ts`).
 *
 * D-06: TUDO — até leitura — delega a `isEscritorioAdmin` (single source of
 * edit). Não há leitura aberta a usuário comum nesta área: a Administração é
 * inteira só-admin.
 */
export const escritorioAdminPermissions = {
  list: (s: SessionContext) => isEscritorioAdmin(s),
  read: (s: SessionContext) => isEscritorioAdmin(s),
  create: (s: SessionContext) => isEscritorioAdmin(s),
  update: (s: SessionContext) => isEscritorioAdmin(s),
  remove: (s: SessionContext) => isEscritorioAdmin(s),
};
